
import { useState, useEffect } from "react";
import { Page } from "@/types/page";
import { useToast } from "@/components/ui/use-toast";

interface UsePageEditorProps {
  page: Page;
  onSave: (page: Page) => void;
}

export const usePageEditor = ({ page, onSave }: UsePageEditorProps) => {
  const [editedPage, setEditedPage] = useState<Page>({
    ...page,
    platform: page.platform || "both",
    position: page.position || "left",
    rotationTimeMs: page.rotationTimeMs || 10, // Default to 10 seconds if not set
    useCustomRotationTime: page.useCustomRotationTime || false
  });
  
  const { toast } = useToast();

  // Debug logging
  useEffect(() => {
    console.log("PageEditor initialized with page:", page);
  }, []);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Page title changed:", e.target.value);
    setEditedPage({
      ...editedPage,
      title: e.target.value
    });
  };

  const handleContentChange = (field: string, value: any) => {
    console.log(`Page content field "${field}" changed:`, value);
    if (field) {
      // If a specific field is provided, update that field
      setEditedPage({
        ...editedPage,
        content: {
          ...editedPage.content,
          [field]: value
        }
      });
    } else {
      // If no field is provided, replace the entire content object
      setEditedPage({
        ...editedPage,
        content: value
      });
    }
  };

  const handlePlatformChange = (platform: any) => {
    console.log("Page platform changed:", platform);
    setEditedPage({
      ...editedPage,
      platform
    });
  };

  const handlePositionChange = (position: any) => {
    console.log("Page position changed:", position);
    setEditedPage({
      ...editedPage,
      position
    });
  };

  const handleRotationTimeChange = (value: number[]) => {
    console.log("Rotation time changed:", value[0]);
    setEditedPage({
      ...editedPage,
      rotationTimeMs: value[0]
    });
  };

  const handleRotationTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      console.log("Rotation time input changed:", value);
      setEditedPage({
        ...editedPage,
        rotationTimeMs: value
      });
    }
  };

  const handleUseCustomRotationTimeChange = (checked: boolean) => {
    console.log("Use custom rotation time changed:", checked);
    setEditedPage({
      ...editedPage,
      useCustomRotationTime: checked
    });
  };

  const handleSavePage = () => {
    if (!editedPage.title.trim()) {
      toast({
        variant: "destructive",
        title: "שגיאה",
        description: "כותרת הדף לא יכולה להיות ריקה"
      });
      return;
    }
    
    console.log("Saving page:", editedPage);
    onSave(editedPage);
  };

  return {
    editedPage,
    handleTitleChange,
    handleContentChange,
    handlePlatformChange, 
    handlePositionChange,
    handleRotationTimeChange,
    handleRotationTimeInputChange,
    handleUseCustomRotationTimeChange,
    handleSavePage
  };
};
